import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

/**
 * Rows and columns of dots, arriving one row at a time.
 *
 * The staggered entry is the teaching, not decoration: seeing seven rows land
 * one after another is what turns "7 × 8" from a fact into a count. A child can
 * follow along out loud, which is exactly what you want them doing.
 */
export function ArrayFigure({ figure }: FigureProps<Figure>) {
  const rows = figure.rows ?? 0;
  const cols = figure.cols ?? 0;
  if (rows < 1 || cols < 1) return null;

  const total = rows * cols;
  // Dots shrink as the grid grows, so a 3 × 4 reads big and a 9 × 9 still fits
  // the width of a tablet held in one hand.
  const dot = total > 60 ? 12 : total > 30 ? 16 : 20;

  return (
    <div
      className="flex flex-col items-center gap-[6px]"
      role="img"
      aria-label={`${rows} rows of ${cols}, which is ${total} in total.`}
    >
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-[6px]">
          {Array.from({ length: cols }, (_, c) => (
            <span
              key={c}
              className="dot-in rounded-full bg-cyan"
              style={{
                width: dot,
                height: dot,
                // By row, not by dot: a hundred separately-timed dots reads as
                // static, a row landing at a time reads as counting.
                animationDelay: `${r * 0.12}s`,
              }}
            />
          ))}
        </div>
      ))}
      <p className="disp mt-2 text-[19px] text-gold">
        {rows} × {cols} = {total}
      </p>
    </div>
  );
}
